import { useQueryClient } from "@tanstack/react-query";
import { useAtom, useAtomValue } from "jotai";
import { renderErrorToast } from "../../../lib/ErrorToastMessage";
import { CALENDAR_CONNECT_ERROR } from "../../constants";
import { baseAtom } from "../../stores/jotai/base";
import { useGoogleLogin } from "../../tanstackQuery/social-integrations/handle-google";

import { TUserAuthStatusResponse } from "../../tanstackQuery/social-integrations/types";
import ConnectCalendar from "./GoogleAndZoom/ConnectCalendar";
import GoogleAndZoomLoggedIn from "./GoogleAndZoom/GoogleAndZoomLoggedIn";
import GoogleLoggedInWithoutZoom from "./GoogleAndZoom/GoogleLoggedInWithoutZoom";
import GoogleLoginMessage from "./GoogleAndZoom/GoogleLoginMessage";
import ShowPermissionError from "./GoogleAndZoom/ShowPermissionError";
import ZoomLoginMessage from "./GoogleAndZoom/ZoomLoginMessage";
import { GoogleAuthLoginResponse, ZoomAuthLoginResponse } from "./GoogleAndZoom/types";
import { zoomIntegrationStateAtom } from "../../stores/jotai/calender&Zoom";
import { useZoomLogin } from "../../tanstackQuery/social-integrations/handle-zoom";


type CalendarFrameProps = {
  isAuthLoading: boolean;
  authStatus: TUserAuthStatusResponse;
}

const CalendarFrame = ({isAuthLoading, authStatus}: CalendarFrameProps) => {
  const base = useAtomValue(baseAtom);
  const [zoomIntegration, setZoomIntegration] = useAtom(
    zoomIntegrationStateAtom
  );
  
  const client = useQueryClient()
  const { mutate: loginWithGoogle } = useGoogleLogin({
    onSuccess(res: GoogleAuthLoginResponse) {
      if(!res.isSuccessGoogleLogin) {
        if(res.errorMessage === CALENDAR_CONNECT_ERROR) {
          renderErrorToast(res.errorMessage)
        }

        return;
      }

      client.invalidateQueries({queryKey: ["user-auth-status"]})
      setZoomIntegration((prev) => ({
        ...prev,
        googleMessageView: true,
        zoomPermissionErrorView: false
      }));
    }
  });
    const { mutate: loginWithZoom } = useZoomLogin({
    onSuccess: (data: ZoomAuthLoginResponse) => {
      client.invalidateQueries({queryKey: ["user-auth-status"]})
      // if(!data.isSuccess) {
      //   if(data.errorMessage === CALENDAR_CONNECT_ERROR) {
      //     return renderErrorToast(data.errorMessage)
      //   }
      // }
      setZoomIntegration(prev => ({
        ...prev,
        googleMessageView: false,
        zoomMessageView: true,
        zoomPermissionErrorView: !data.isPermissionAllowed,
        zoomLicesePermisson: !data.isLicensedUser,
        errorMessage:data.errorMessage
       }))
    }
  });

  const zoomLogin = () => {
    loginWithZoom({
      userID: base?.user?.userID,
      teamID: base?.team?.id
    })
  };

  const googleLogin = () => {
    loginWithGoogle({
      userID: base?.user?.userID,
      teamID: base?.team?.id
    })
      
  };
  
  const disableZoomLoginMessage = () => {
      setZoomIntegration(prev => ({
        ...prev,
        zoomMessageView: false,
      }))
  };

  const handleZoomLoginLater = () => {
    setZoomIntegration(prev => ({
      ...prev,
      zoomMessageView: false,
      googleMessageView: false,
    }))
  };

  // if(isAuthLoading) return <LinearProgress />;

  let renderView = null;

  if((!authStatus?.isGoogleLoggedIn && !authStatus?.isZoomLoggedIn) || (authStatus?.isZoomLoggedIn && !authStatus?.isGoogleLoggedIn)) {
    renderView = <ConnectCalendar googleLogin={googleLogin} />;
  }
  else if(zoomIntegration.errorMessage === CALENDAR_CONNECT_ERROR){
    renderView = <ShowPermissionError zoomLogin={zoomLogin}
    title = "Connection Failed!"
    description = "Your Calendar and Zoom emails don't match. Please reconnect using the same email address."
    />
  }
  else if(zoomIntegration.zoomPermissionErrorView) {
    renderView = <ShowPermissionError zoomLogin={zoomLogin}
    title = "Connection Failed!"
    description = "We need additional permissions to access your Zoom cloud recordings. Please try connecting again and grant all requested permissions."
    />
  }
  else if(zoomIntegration.zoomLicesePermisson){
    renderView = <ShowPermissionError zoomLogin={zoomLogin} 
      title = "Premium Zoom Subscription Required!"
      description = "Your current Zoom plan doesn't support cloud recording. Please upgrade your Zoom plan to continue."
    />
  }

  else if(authStatus?.isGoogleLoggedIn && zoomIntegration.googleMessageView && !authStatus?.isZoomLoggedIn) {
    renderView = <GoogleLoginMessage zoomLogin={zoomLogin} onLater={handleZoomLoginLater} />
  }

  else if(authStatus?.isGoogleLoggedIn && !authStatus?.isZoomLoggedIn) {
    renderView = <GoogleLoggedInWithoutZoom onConnectZoom={zoomLogin} />;
  }

  else if(authStatus?.isZoomLoggedIn && zoomIntegration.zoomMessageView) {
    renderView = <ZoomLoginMessage disableZoomLoginMessage={disableZoomLoginMessage} />
  }

  else if(authStatus?.isGoogleLoggedIn && authStatus?.isZoomLoggedIn) {
    renderView = <GoogleAndZoomLoggedIn />;
  }

  return <>{renderView}</>;
};

export default CalendarFrame;

